import type { AdminLocale } from '../../content/locale.js';
import { ApiError, HTTP } from '../../lib/http.js';

/* Mensagens do CRUD de documentos legais (ADR-0774). O `mapHttpError` preserva o código do
   backend em `serverCode`; o código específico manda e o status entra só como rede de segurança.

   O 409 de imutabilidade é o caso que MAIS importa. Ele não é "falha ao salvar" — é o contrato
   dizendo que texto publicado não se edita. A mensagem tem de dizer o que fazer NO LUGAR (criar a
   versão N+1 copiando a atual), senão o operador fica preso achando que a tela está quebrada.

   `locale` é OPCIONAL (default pt-BR) e não um `Bilingual<T>`/`useBilingual`: `legalErrorMessage`
   também é consumido por `lib/legal-api.ts` e `routes/legal-page.tsx` — frentes que ainda chamam
   com um único argumento (mesma razão de `releases-error.ts`/`pack-catalog-labels.ts`). */

interface LegalErrorStrings {
  readonly versionImmutable: string;
  readonly versionConflict: string;
  readonly sourceVersionNotFound: string;
  readonly createContentRequired: string;
  readonly versionNotFound: string;
  readonly docKeyUnknown: string;
  readonly publishBodyInvalid: string;
  readonly networkFailure: string;
  readonly forbidden: string;
  readonly conflictFallback: string;
  readonly genericFailure: string;
  readonly unexpected: string;
}

const LEGAL_ERROR_STRINGS: Readonly<Record<AdminLocale, LegalErrorStrings>> = {
  'pt-BR': {
    versionImmutable:
      'Esta versão já está publicada e é imutável. Para corrigir o texto, crie a próxima versão a partir dela ("Nova versão a partir desta") e publique a nova — o histórico do que vigorou não é reescrito.',
    versionConflict:
      'Outra pessoa criou uma versão ao mesmo tempo. Recarregue a lista e crie de novo — a sua será a versão seguinte.',
    sourceVersionNotFound:
      'A versão escolhida para copiar não existe mais. Recarregue a lista e escolha outra origem.',
    createContentRequired:
      'Informe título e conteúdo de pelo menos um idioma, ou escolha uma versão para copiar.',
    versionNotFound:
      'Esta versão não existe mais — pode ter sido removida em outra aba. Recarregue a lista.',
    docKeyUnknown: 'Documento legal desconhecido.',
    publishBodyInvalid:
      'Data de vigência inválida. Informe uma data e hora válidas ou publique com vigência imediata.',
    networkFailure: 'Falha de conexão. Tente novamente.',
    forbidden:
      'Você não tem permissão para gerenciar documentos legais (somente super_admin) — ou sua sessão expirou.',
    conflictFallback:
      'Conflito de estado: versão publicada não pode ser editada nem removida. Crie a versão seguinte a partir dela.',
    genericFailure: 'Não foi possível concluir a operação. Tente novamente.',
    unexpected: 'Erro inesperado.',
  },
  'en-US': {
    versionImmutable:
      'This version is already published and is immutable. To fix the text, create the next version from it ("New version from this one") and publish the new one — the history of what was in effect is never rewritten.',
    versionConflict:
      'Someone else created a version at the same time. Reload the list and create again — yours will be the next version.',
    sourceVersionNotFound:
      'The version chosen to copy no longer exists. Reload the list and choose another source.',
    createContentRequired:
      'Enter title and content for at least one language, or choose a version to copy.',
    versionNotFound:
      'This version no longer exists — it may have been removed in another tab. Reload the list.',
    docKeyUnknown: 'Unknown legal document.',
    publishBodyInvalid:
      'Invalid effective date. Enter a valid date and time or publish with immediate effect.',
    networkFailure: 'Connection failure. Try again.',
    forbidden:
      'You do not have permission to manage legal documents (super_admin only) — or your session expired.',
    conflictFallback:
      'State conflict: a published version cannot be edited or removed. Create the next version from it.',
    genericFailure: 'Could not complete the operation. Try again.',
    unexpected: 'Unexpected error.',
  },
};

function specificMessage(code: string, strings: LegalErrorStrings): string | null {
  switch (code) {
    case 'legal_version_immutable':
      return strings.versionImmutable;
    case 'legal_version_conflict':
      return strings.versionConflict;
    case 'legal_source_version_not_found':
      return strings.sourceVersionNotFound;
    case 'legal_create_content_required':
      return strings.createContentRequired;
    case 'legal_version_not_found':
      return strings.versionNotFound;
    case 'legal_doc_key_unknown':
      return strings.docKeyUnknown;
    case 'legal_publish_body_invalid':
      return strings.publishBodyInvalid;
    default:
      return null;
  }
}

export function legalErrorMessage(err: unknown, locale: AdminLocale = 'pt-BR'): string {
  const strings = LEGAL_ERROR_STRINGS[locale];
  if (err instanceof ApiError) {
    const specific = specificMessage(err.serverCode, strings);
    if (specific !== null) return specific;
    if (err.code === 'network' || err.status === 0) {
      return strings.networkFailure;
    }
    if (err.status === HTTP.unauthorized || err.status === HTTP.forbidden) {
      return strings.forbidden;
    }
    /* 409 sem código reconhecido: ainda assim é conflito de ESTADO, e o único conflito possível
       neste domínio é mexer no que já foi publicado. Aponta a saída em vez de culpar a rede. */
    if (err.status === HTTP.conflict) {
      return strings.conflictFallback;
    }
    return strings.genericFailure;
  }
  return strings.unexpected;
}
